import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { Button } from '../components/ui/button';

function WhatsAppStatusDot() {
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  const fetch_ = useCallback(async () => {
    try {
      const r = await fetch('/api/whatsapp/status', { credentials: 'include' });
      const d = await r.json();
      setStatus(d.status);
    } catch { setStatus('service_unavailable'); }
  }, []);

  useEffect(() => { fetch_(); const iv = setInterval(fetch_, 30000); return () => clearInterval(iv); }, [fetch_]);

  if (!status || status === 'loading') return null;

  const cfg = {
    connected:           { dot: 'bg-green-500', text: 'WhatsApp activo',       clickable: false },
    qr:                  { dot: 'bg-yellow-400 animate-pulse', text: 'Escanear QR', clickable: true },
    disconnected:        { dot: 'bg-red-500',   text: 'WhatsApp desconectado', clickable: true },
    service_unavailable: { dot: 'bg-red-500',   text: 'Servicio WhatsApp caído', clickable: false },
  }[status] ?? { dot: 'bg-gray-400', text: 'WhatsApp...', clickable: false };

  return (
    <div
      onClick={() => cfg.clickable && navigate('/admin/settings')}
      className={`flex items-center gap-2 px-3 py-2 rounded-md mb-1 ${cfg.clickable ? 'cursor-pointer hover:bg-[#F5F2ED]' : ''}`}
      title={cfg.clickable ? 'Ir a Ajustes para reconectar' : cfg.text}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      <span className="text-xs text-[#8A9BA8] truncate">{cfg.text}</span>
    </div>
  );
}

const LOGO_URL = "/logo_surfskull.png";

const adminNavItems = [
  { to: '/admin', icon: '📊', label: 'Dashboard', end: true },
  { to: '/admin/courses', icon: '🎓', label: 'Cursos' },
  { to: '/admin/rentals', icon: '🌊', label: 'Alquileres' },
  { to: '/admin/reservations', icon: '📋', label: 'Reservas' },
  { to: '/admin/schedule', icon: '📅', label: 'Programación' },
  { to: '/admin/income', icon: '💰', label: 'Ingresos' },
  { to: '/admin/expenses', icon: '💸', label: 'Gastos' },
  { to: '/admin/crm', icon: '👥', label: 'Clientes' },
  { to: '/admin/monitors', icon: '🏄', label: 'Monitores' },
  { to: '/admin/invoicing', icon: '🧾', label: 'Facturas' },
  { to: '/admin/inventory', icon: '📦', label: 'Inventario' },
  { to: '/admin/excel-skull', icon: '📈', label: 'Excel Skull', adminOnly: true },
  { to: '/admin/settings', icon: '⚙️', label: 'Ajustes' },
];

const casetaNavItems = [
  { to: '/admin/crm', icon: '👥', label: 'Clientes', end: true },
  { to: '/admin/schedule', icon: '📅', label: 'Programación' },
  { to: '/admin/reservations', icon: '📋', label: 'Reservas' },
  { to: '/admin/rentals', icon: '🌊', label: 'Alquileres' },
  { to: '/admin/courses', icon: '🎓', label: 'Cursos' },
  { to: '/admin/inventory', icon: '📦', label: 'Inventario' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => { const main = mainRef.current; if (!main) return; const fn = () => setShowScrollTop(main.scrollTop > 300); main.addEventListener('scroll', fn); return () => main.removeEventListener('scroll', fn); }, []);

  const navItems = (user?.role === 'caseta' ? casetaNavItems : adminNavItems).filter(item => !item.adminOnly || user?.role === 'admin');

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="h-screen bg-[#FCFBF9] flex overflow-hidden" data-testid="admin-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#FCFBF9] border-r border-[#E5E0D8] transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col`}>
        <div className="p-4 border-b border-[#E5E0D8]">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SURF SKULL" className="h-10 w-auto rounded-lg" />
            <div>
              <h2 className="text-base font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>SURF ⚡ SKULL</h2>
              <p className="text-xs text-[#8A9BA8]">Contabilidad</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${isActive ? 'bg-[#4A6D7C] text-white' : 'text-[#4A5D6C] hover:bg-[#F5F2ED]'}`}
              data-testid={`nav-${item.label.toLowerCase()}`}>
              <span className="text-base leading-none">{item.icon}</span>{item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5E0D8]">
          {user?.role === 'admin' && <WhatsAppStatusDot />}
          <div className="flex items-center gap-3 mb-3 px-3">
            <div className="w-8 h-8 rounded-full bg-[#4A6D7C] flex items-center justify-center text-white text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || 'A'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A2B3C] truncate">{user?.name}</p>
              <p className="text-xs text-[#8A9BA8] truncate">{user?.role}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button" className="w-full justify-start text-[#E07A5F] hover:bg-[#E07A5F]/10"><LogOut className="w-4 h-4 mr-2" />Cerrar Sesión</Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="glass-nav sticky top-0 z-20 flex items-center justify-between px-6 py-3 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
          <h2 className="text-lg font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>SURF ⚡ SKULL</h2>
          <div className="w-10" />
        </header>
        <main ref={mainRef} className="flex-1 px-4 md:px-8 pb-4 md:pb-8 overflow-y-auto overflow-x-hidden"><Outlet /></main>
      </div>
      {showScrollTop && (<button onClick={() => mainRef.current?.scrollTo({top:0,behavior:'smooth'})} className="fixed bottom-6 right-6 z-50 bg-[#4A6D7C] text-white rounded-full shadow-lg p-3 hover:bg-[#3A5663] transition-all" title="Volver arriba"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg></button>)}
    </div>
  );
}
